import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Alpine from 'alpinejs';

// THEME MODULES
import Carousels from './modules/carousels.js';
import setupAnimationUtilities from './modules/animation-utilities.js';
import { utils } from './modules/utils.js';

// Add GSAP to window and Register plugins
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
window.Alpine = Alpine
gsap.registerPlugin(ScrollTrigger);

// Start Alpine.js after DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  Alpine.start();
  Carousels();
});

const { select, selectAll } = utils;

// Use matchHeight for headers
function matchHeights() {
  const pfMain = document.querySelector('body #page');
  const wpadminbar = document.querySelector('#wpadminbar');
  const siteHeader = document.querySelector('#main-nav');
  const offcanvas = document.querySelector('.offcanvas-end');
  const offcanvasBody = document.querySelector('.offcanvas-body');
  const wpBlockCovers = document.querySelectorAll('.wp-block-cover.pf-header-offset');

  // Adjust page padding top based on site header height
  pfMain.style.paddingTop = siteHeader.offsetHeight + 'px';

  // Adjust for admin bar if present
  if (document.body.classList.contains('admin-bar') && wpadminbar) {
    document.querySelector('#main-nav').style.top = wpadminbar.offsetHeight + 'px';
    pfMain.style.paddingTop = siteHeader.offsetHeight + 'px';
  }

  // Adjust .wp-block-cover height if it has pf-header-offset class
  wpBlockCovers.forEach((cover) => {
    const headerHeight = siteHeader.offsetHeight;
    // If admin bar is present, include its height in the calculation
    const adminBarHeight = wpadminbar ? wpadminbar.offsetHeight : 0;
    const totalOffset = headerHeight + adminBarHeight;

    // Subtract the height of the nav (and admin bar, if present) from the cover's height
    cover.style.minHeight = `calc(100svh - ${totalOffset}px)`;
  });

  // Optionally, handle offcanvas logic if needed
  // if (offcanvas) {
  //   let totalOffset = siteHeader.offsetHeight;
  //   if (document.body.classList.contains('admin-bar') && wpadminbar) {
  //     totalOffset += wpadminbar.offsetHeight;
  //   }
  //   offcanvas.style.top = totalOffset + 'px';
  //   if (offcanvasBody) {
  //     offcanvasBody.style.maxHeight = `calc(100vh - ${totalOffset}px)`;
  //   }
  // }
}

// Hamburger logic for Bootstrap 5 Offcanvas navs
const offcanvasElement = document.querySelector("#navbarNavOffcanvas");
offcanvasElement.addEventListener("hide.bs.offcanvas", function () {
  document.querySelector(".navbar-toggler").classList.remove("is-active");
  document.querySelector("#main-nav").classList.remove("mobile-menu-open");
});
offcanvasElement.addEventListener("show.bs.offcanvas", function () {
  document.querySelector(".navbar-toggler").classList.add("is-active");
  document.querySelector("#main-nav").classList.add("mobile-menu-open");
});

// ScrollTrigger setup for sticky image
const images = gsap.utils.toArray(".pf-sticky-images figure");
const numImages = images.length;
const section = document.querySelector(".pf-sticky-section");
const tl = gsap.timeline({
  scrollTrigger: {
    trigger: section,
    start: "top top",
    end: "bottom bottom",
    scrub: true,
    pin: ".pf-sticky-images",
    pinSpacing: false,
    markers: false
  }
});
const scrollInterval = 1 / (numImages - 1);
images.forEach((image, i) => {
  if (i !== numImages - 1) {
    const fadeOutStart = scrollInterval * i;
    const fadeDuration = scrollInterval * 0.5;
    gsap.set(image, { zIndex: numImages - i });
    tl.to(image, { autoAlpha: 0, duration: fadeDuration }, fadeOutStart);
  }
});

// Recreate Headroom with ScrollTrigger
const mainNav = document.querySelector('#main-nav');
let header_progress = 0;
let header_direction = 1;
ScrollTrigger.create({
  trigger: "#page",
  start: "+=100",
  end: 'bottom bottom',
  onUpdate: function (self) {
    header_progress = self.progress.toFixed(2);
    header_direction = self.direction;
    if (header_progress == 0.00) {
      // scroll is at top
      mainNav.classList.add('pf-top');
      mainNav.classList.remove('pf-notTop');
      document.body.classList.remove('pf-is-scrolled');
    } else {
      // page has been scrolled
      mainNav.classList.remove('pf-top');
      mainNav.classList.add('pf-notTop');
      document.body.classList.add('pf-is-scrolled');
    }
    if (header_direction == -1) {
      // user is scrolling up
      mainNav.classList.add('pf-pinned');
    } else {
      // user is scrolling down
      mainNav.classList.remove('pf-pinned');

      // close menu on scroll down - optional
      if(select('#main-nav').classList.contains('mobile-menu-open')){
        select('.navbar-toggler').click()
      }

      // // close all open dropdowns on scroll down
      // utils.selectForEach('.dropdown-menu.show', (dropdown) => {
      //   const dropdownToggle = dropdown.closest('.dropdown').querySelector('[data-bs-toggle="dropdown"]');
      //   if (dropdownToggle && dropdown.classList.contains('show')) {
      //     dropdown.classList.remove('show');
      //     dropdownToggle.setAttribute('aria-expanded', 'false');
      //     const closeButton = dropdown.querySelector('.pf-close-btn');
      //     if (closeButton) {
      //       closeButton.click();
      //     }
      //   }
      // });
    }
  }
});

// Sticky timeline heading 
const timelineSection = document.querySelector(".pf-timeline-col");
const timelineHeading = document.querySelector(".timeline-heading");

// Check if timelineSection and timelineHeading exist before running GSAP
if (timelineSection && timelineHeading) {
  const headingHeight = timelineHeading.offsetHeight + 100;

  gsap.timeline({
    scrollTrigger: {
      trigger: timelineSection,
      start: "top-=75 top", 
      end: () => `bottom-=${headingHeight}px top`,
      scrub: true,
      pin: timelineHeading,
      pinSpacing: false,
      markers: false
    }
  });
}

// Select all timeline items
const timelineItems = document.querySelectorAll('.timeline-item');
const timelineContainer = document.querySelector('.pf-timeline');

// Check if there are timeline items and the container exists
if (timelineItems.length > 0 && timelineContainer) {
  const lastLead = timelineItems[timelineItems.length - 1]?.querySelector('p.lead'); // Select the last p.lead
  const singleTimelineNumber = timelineItems[0]?.querySelector('.timeline-number'); // Select one .timeline-number

  // Check if lastLead and singleTimelineNumber exist
  if (lastLead && singleTimelineNumber) {
    
    // Calculate the dynamic value we need to subtract
    function calculateSubtractionHeight() {
      const lastLeadHeight = lastLead.offsetHeight;
      const halfTimelineNumberHeight = singleTimelineNumber.offsetHeight;
      
      // Return the total subtraction value (last p.lead height + half of timeline-number)
      return lastLeadHeight + halfTimelineNumberHeight + 30;
    }

    // Animate the timeline loop
    timelineItems.forEach((item, index) => {
      const number = item.querySelector('.timeline-number');
      const description = item.querySelector('p');
      
      // Check if the number and description exist
      if (number && description) {
        gsap.set([number, description], { autoAlpha: 0, y: 50 });

        ScrollTrigger.create({
          trigger: item,
          start: 'top bottom',
          onEnter: () => {
            gsap.to(number, {
              autoAlpha: 1,
              y: 0,
              duration: 0.5,
              ease: 'power2.out',
              delay: index * 0.5
            });
            gsap.to(description, {
              autoAlpha: 1,
              y: 0,
              duration: 0.5,
              ease: 'power2.out',
              delay: (index * 0.5) + 0.5
            });

            const subtractionHeight = calculateSubtractionHeight();
            const lineGrowth = `calc(${((index + 1) / timelineItems.length) * 100}% - ${subtractionHeight}px)`;
            
            gsap.to(timelineContainer, {
              '--line-height': lineGrowth,
              duration: 4,
              ease: 'power2.out'
            });
          },
        });
      }
    });
  }
}

// GSAP CountUp with Prefix, Suffix, and Number Formatting (commas)
let pfCountElements = document.querySelectorAll(".pf-count");
if (pfCountElements.length) {
  pfCountElements.forEach(function (element) {
    var count = element,
      zero = { val: 0 },
      num = parseFloat(count.getAttribute("data-number")),
      prefix = count.getAttribute("data-prefix") || '',
      suffix = count.getAttribute("data-suffix") || '',
      split = (num + "").split("."),
      decimals = split.length > 1 ? split[1].length : 0;

    gsap.to(zero, {
      val: num,
      duration: 2.5,
      ease: "power2.out",
      scrollTrigger: element,
      onUpdate: function () {
        // Format the number with commas and decimal places
        let formattedNumber = zero.val.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        count.textContent = prefix + formattedNumber + suffix;
      }
    });
  });
}

// GSAP Parallax BG
let pfParallaxBgElements = document.querySelectorAll(".pf-parallax-bg");
pfParallaxBgElements.forEach(function(parallax) {
  gsap.fromTo(parallax.querySelector("img"), 
    { yPercent: 3 },   // starting position
    {
      yPercent: -12,   // end position
      ease: "none",
      scrollTrigger: {
        trigger: parallax,
        start: "top bottom",
        end: "bottom top",
        scrub: true
      }
    }
  );
});

// Add wrapper to force inline nav buttons
const navButtons = document.querySelectorAll('.nav-button');
if (navButtons.length > 0) {
  const wrapperDiv = document.createElement('div');
  wrapperDiv.classList.add('nav-button-wrapper', 'mt-4', 'mt-lg-0');
  navButtons.forEach(button => {
    wrapperDiv.appendChild(button);
  });
  const navbar = document.querySelector('.navbar-nav');
  navbar.appendChild(wrapperDiv);
}

// Change dropdown functionality
function dropdownFade() {
  const dropdowns = document.querySelectorAll('.dropdown');
  const searchDropdown = document.querySelector('.menu-item-search');

  if (window.innerWidth >= 992 && !utils.isTouchDevice()) {
    // Desktop behavior
    dropdowns.forEach(dropdown => {
      const dropdownMenu = dropdown.querySelector('.dropdown-menu');
      const dropdownToggle = dropdown.querySelector('[data-bs-toggle="dropdown"]');
      const closeBtn = dropdown.querySelector('.pf-close-btn');

      if (dropdownToggle) {
        // Restore original href on desktop
        const originalHref = dropdownToggle.dataset.originalHref || dropdownToggle.href;
        dropdownToggle.href = originalHref !== '#' ? originalHref : '';
      }

      if (dropdown === searchDropdown) {
        // For menu-item-search, apply click functionality with fade-in effect
        dropdownToggle.addEventListener('click', function (e) {
          e.preventDefault();
          const isOpen = dropdown.classList.contains('show');

          // Toggle dropdown manually on click
          if (isOpen) {
            dropdown.classList.remove('show');
            dropdownMenu.classList.remove('show', 'pf-show');
            dropdownToggle.setAttribute('aria-expanded', 'false');
          } else {
            dropdown.classList.add('show');
            dropdownMenu.classList.add('show');
            dropdownToggle.setAttribute('aria-expanded', 'true');

            // Add fade-in effect with a slight delay
            setTimeout(function () {
              dropdownMenu.classList.add('pf-show');
            }, 10);
          }
        });

        // Close the dropdown when the pf-close-btn is clicked
        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            dropdown.classList.remove('show');
            dropdownMenu.classList.remove('show', 'pf-show');
            dropdownToggle.setAttribute('aria-expanded', 'false');
          });
        }
      } else {
        // For other dropdowns, apply hover behavior on desktop
        dropdown.addEventListener('mouseenter', function () {
          // Close the search dropdown if it's open
          if (searchDropdown.classList.contains('show')) {
            const searchDropdownMenu = searchDropdown.querySelector('.dropdown-menu');
            const searchDropdownToggle = searchDropdown.querySelector('[data-bs-toggle="dropdown"]');
            searchDropdown.classList.remove('show');
            searchDropdownMenu.classList.remove('show', 'pf-show');
            if (searchDropdownToggle) {
              searchDropdownToggle.setAttribute('aria-expanded', 'false');
            }
          }

          // Show this dropdown on hover
          if (!dropdown.classList.contains('show')) {
            dropdown.classList.add('show');
            dropdownMenu.classList.add('show');
            if (dropdownToggle) {
              dropdownToggle.setAttribute('aria-expanded', 'true');
            }
          }
        });

        // Hide dropdown when mouse leaves
        dropdown.addEventListener('mouseleave', function () {
          if (dropdown.classList.contains('show')) {
            dropdown.classList.remove('show');
            dropdownMenu.classList.remove('show', 'pf-show');
            if (dropdownToggle) {
              dropdownToggle.setAttribute('aria-expanded', 'false');
            }
          }
        });

        // Add fade-in effect
        dropdown.addEventListener('mouseenter', function () {
          setTimeout(function () {
            dropdownMenu.classList.add('pf-show');
          }, 10);
        });

        dropdown.addEventListener('mouseleave', function () {
          dropdownMenu.classList.remove('pf-show');
        });
      }
    });
  } else {
    var dropdownLinks = document.querySelectorAll('.dropdown-toggle');

    dropdownLinks.forEach(function (link) {
      link.dataset.originalHref = link.href;
      link.href = '#';

      link.addEventListener('click', function (e) {
        e.preventDefault();
        const dropdown = link.closest('.dropdown');
        const dropdownMenu = dropdown.querySelector('.dropdown-menu');
        const isOpen = dropdown.classList.contains('show');

        if (isOpen) {
          dropdown.classList.remove('show');
          dropdownMenu.classList.remove('show', 'pf-show');
          link.setAttribute('aria-expanded', 'false');
        } else {
          dropdown.classList.add('show');
          dropdownMenu.classList.add('show', 'pf-show');
          link.setAttribute('aria-expanded', 'true');

          // Add fade-in effect with a slight delay
          setTimeout(function () {
            dropdownMenu.classList.add('pf-show');
          }, 10);
        }
      });
    });
  }
}

setupAnimationUtilities();
matchHeights();
dropdownFade();

// Event listeners and triggers
window.addEventListener('load', () => {
  // On load functions
});

// Resize with debounce - Let's debounce our heavy lifts
window.addEventListener('resize', utils.debounce(() => {
  matchHeights();

  if (!utils.isTouchDevice()) {
    ScrollTrigger.refresh();
    dropdownFade();
  }
}, 250));


